import { ScoreCard } from "@foodpilot/foods";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { Box, Chip, Stack, SvgIcon, Tooltip, Typography, useTheme } from "@mui/material";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { useResponsiveBreakpoints } from "src/utils/useResponsiveBreakpoints";
import { Suspense } from "../utils/Suspense";
import { OlapScoresWithPrevScores, ScoreSourceMeta } from "src/api/client-api/olapScores/olapScoresSchema";
import type {
    IngredientScoreSourceMetaPayload,
    TransportScoresPayload,
} from "src/api/client-api/olapScores/olapScoresSchema";
import { resolveRowContributors } from "src/api/client-api/olapScores/olapScoresSchema";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { ScoreSummary } from "../Score/ScoreCardContent/ScoreSummary";
import { ScoreSourceChip } from "../Score/ScoreSourceChip";
import { useTranslation } from "react-i18next";
import { ScoreLoadingSkeleton } from "../Score/ScoreLoadingSkeleton";

type TransportHypothesis = NonNullable<TransportScoresPayload["byScoreId"][string]["stages"][number]>;
type TransportLegHypothesis = NonNullable<TransportHypothesis["legs"]>[number];
type TransportLegDisplay = {
    stage: TransportHypothesis;
    leg: TransportLegHypothesis;
    index: number;
};

const formatTransportValue = (value: number | null | undefined, precision: number): string => {
    if (value === null || value === undefined) return "";
    if (value === 0) return "0";

    return value.toLocaleString(undefined, {
        maximumFractionDigits: Math.max(precision, 4),
        maximumSignificantDigits: 3,
    });
};

const formatDistance = (distanceKm: number | null | undefined): string | null => {
    if (distanceKm === null || distanceKm === undefined) return null;

    return `${distanceKm.toLocaleString(undefined, { maximumFractionDigits: 0 })} km`;
};

const resolveModeIcon = (leg: TransportLegHypothesis): typeof SvgIcon => {
    const name = leg.name?.toLowerCase() ?? "";
    if (name.includes("plane") || name.includes("air") || name.includes("avion")) return FlightTakeoffIcon;
    if (name.includes("ship") || name.includes("boat") || name.includes("sea")) return DirectionsBoatIcon;

    return LocalShippingIcon;
};

const resolveLegIcon = (leg: TransportLegHypothesis) => {
    const ModeIcon = resolveModeIcon(leg);

    if (!leg.isRefrigerated) {
        return <ModeIcon />;
    }

    return (
        <Box component="span" sx={{ position: "relative", display: "inline-flex", width: 24, height: 18 }}>
            <ModeIcon sx={{ fontSize: 18, position: "absolute", right: 0, bottom: -1 }} />
            <AcUnitIcon sx={{ fontSize: 13, position: "absolute", left: -2, top: -4 }} />
        </Box>
    );
};

const resolveRouteDestination = (leg: TransportLegHypothesis, stage: TransportHypothesis): string | null => {
    if (leg.destinationCountryCode ?? stage.destinationCountryCode) {
        return leg.destinationCountryCode ?? stage.destinationCountryCode ?? null;
    }

    if (stage.stage !== "ingredient_to_processing_site" && stage.circuitName?.startsWith("ecobalyse-")) {
        return "FR";
    }

    return null;
};

const formatCountryCode = (code: string): string => {
    return code === "ROW" ? "RoW" : code;
};

const resolveLegLabel = (leg: TransportLegHypothesis, stage: TransportHypothesis): string => {
    const distance = formatDistance(leg.distanceKm);
    const destination = resolveRouteDestination(leg, stage);
    const rawOrigin = leg.originCountryCode ?? stage.originCountryCode;
    const origin = rawOrigin === "NULL" ? null : rawOrigin;
    const route = origin && destination ? `${formatCountryCode(origin)} → ${formatCountryCode(destination)}` : null;
    const mode =
        leg.name?.includes("plane") || leg.name?.includes("air") ? "Air"
        : leg.name?.includes("ship") || leg.name?.includes("boat") || leg.name?.includes("sea") ? "Sea"
        : "Road";

    return [mode, route, distance, leg.isRefrigerated ? "cooled" : null].filter(Boolean).join(" · ");
};

const resolveTransportLegDisplays = (stages: TransportHypothesis[]): TransportLegDisplay[] => {
    return stages.flatMap((stage) => (stage.legs ?? []).map((leg, index) => ({ stage, leg, index }))).reverse();
};

type ScoreCardsProps = {
    appearance?: "legacy" | "product-v2";
    scores: OlapScoresWithPrevScores | undefined;
    isLoading?: boolean;
    referenceScores?: Record<number, number>;
    referenceName?: string;
    prevCampaignName?: string;
    showSpecificityRate?: boolean;
    showDetails?: boolean;
    showDetailsAction?: (scoreId: number) => void;
    /**
     * Legacy single-source descriptor — kept for non-Ingredient call sites that
     * pre-date the multi-contributor payload. Prefer `sourceMetaPayload` when
     * available so the chip can spell every distinct contributor out (cf.
     * Apr-25 product feedback on mixed sources).
     */
    sourceMeta?: ScoreSourceMeta | null;
    /**
     * Full per-supplier source payload from the OLAP endpoint. When provided,
     * each card's `ScoreSourceChip` renders a multi-source aware tooltip
     * ("Score basé sur la référence fournisseur Valrhona et la référence
     * plateforme."). Pure-SSQ rows produce no chip (precise data path).
     */
    sourceMetaPayload?: IngredientScoreSourceMetaPayload | null;
    transportScores?: TransportScoresPayload | null;
    showUnavailableWhenMissing?: boolean;
};
export const ScoreCards = (props: ScoreCardsProps) => {
    const {
        appearance = "legacy",
        scores,
        isLoading = false,
        referenceScores,
        referenceName,
        prevCampaignName,
        showSpecificityRate = false,
        showDetails = false,
        showDetailsAction,
        sourceMeta,
        sourceMetaPayload,
        transportScores,
        showUnavailableWhenMissing = false,
    } = props;

    const aggregateContributors = sourceMetaPayload ? resolveRowContributors(sourceMetaPayload) : null;
    const resolvedTransportScores = transportScores ?? scores?.transportScores;

    const { t } = useTranslation();
    const theme = useTheme();
    const { scoresWithVariations, selectedVariations, lastSelectedVariation } = useEntityScores();
    const { isMobile, isTablet, isDesktop, isUltrawide } = useResponsiveBreakpoints();
    const isProductV2 = appearance === "product-v2";

    // Responsive grid columns
    const getGridColumns = () => {
        if (isMobile) return "1fr";
        if (isTablet) return "1fr 1fr";
        if (isDesktop) return "1fr 1fr 1fr";
        if (isUltrawide) return "1fr 1fr 1fr";
        return "1fr 1fr 1fr"; // fallback
    };

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: getGridColumns(),
                gridAutoRows: "1fr",
                gap: theme.spacing(3.5),
                // padding: theme.spacing(2, 0),
            }}
        >
            {scoresWithVariations.flatMap((scoreWithVariations) => {
                if (scoreWithVariations.variationId !== null) return [];

                const scoreId = scoreWithVariations.scoreId;
                const mainScore = scores?.olapScores[scoreId];

                const prevMainScore = scores?.olapPrevScores?.[scoreId];
                const scoreValue = OlapScoresService.normalizeNumericalScore(mainScore?.score.value);
                const prevScoreValue = OlapScoresService.normalizeNumericalScore(prevMainScore?.score.value);
                const transportScore = resolvedTransportScores?.byScoreId[String(scoreId)];

                const specificityRateScore = OlapScoresService.getSubscoreWithType("specificity-rate", mainScore);
                const specificityRate = OlapScoresService.normalizeNumericalScore(specificityRateScore?.score.value);

                const variations = scoresWithVariations.filter(
                    (scoreWithVariation) => scoreWithVariation.scoreId === scoreId,
                );

                const selectedVariationId = selectedVariations.query?.[scoreId];
                const selectedVariation = variations.find((variation) => variation.variationId === selectedVariationId);
                const precision = selectedVariation?.precision ?? scoreWithVariations.precision ?? 2;
                const transportUnit = transportScore?.unit ?? selectedVariation?.unit ?? scoreWithVariations.unit;
                const transportValue = formatTransportValue(transportScore?.value, precision);

                const referenceScore = referenceScores?.[scoreId];

                return [
                    // The badge needs an absolutely-positioned anchor on the card; ScoreCard
                    // ships from @foodpilot/foods so we wrap it in a `position: relative`
                    // box rather than fork the design system component.
                    <Box key={scoreId} sx={{ height: isProductV2 ? "100%" : undefined, position: "relative" }}>
                        {(aggregateContributors !== null || sourceMeta) && (
                            <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
                                {aggregateContributors !== null ?
                                    <ScoreSourceChip contributors={aggregateContributors} compact />
                                :   <ScoreSourceChip meta={sourceMeta} compact />}
                            </Box>
                        )}
                        <ScoreCard
                            header={{
                                selectedItemId: selectedVariation?.rowId,
                                options: variations.map((variation) => ({ ...variation, id: variation.rowId })),
                            }}
                            moreDetailAction={() => showDetailsAction?.(scoreId)}
                            disableFooter={showDetails === false || scoreValue === null}
                            seeMoreText={t("scorecard.see-more-details", "See more details")}
                        >
                            <Suspense
                                fallback={isProductV2 ? <ScoreLoadingSkeleton /> : undefined}
                                isLoading={
                                    showUnavailableWhenMissing ?
                                        isLoading &&
                                        (mainScore === undefined || lastSelectedVariation.scoreId === scoreId)
                                    :   mainScore === undefined ||
                                        (isLoading && lastSelectedVariation.scoreId === scoreId)
                                }
                            >
                                <ScoreSummary
                                    appearance={appearance}
                                    score={scoreValue}
                                    unit={selectedVariation?.unit ?? scoreWithVariations.unit}
                                    prevScoreValue={prevScoreValue}
                                    prevCampaign={prevCampaignName}
                                    referenceScore={selectedVariation === undefined ? referenceScore : undefined}
                                    referenceName={selectedVariation === undefined ? referenceName : undefined}
                                    improvement={lastSelectedVariation.improvement}
                                    specificityRate={
                                        showSpecificityRate && specificityRate !== null ? specificityRate : undefined
                                    }
                                    precision={precision}
                                />
                                {!isProductV2 && transportScore && (
                                    <Tooltip
                                        title={
                                            transportScore.status === "calculated" ?
                                                transportScore.stages
                                                    .map((stage) =>
                                                        [
                                                            t(
                                                                `transport.stage.${stage.stage}`,
                                                                stage.stage.replace(/_/g, " "),
                                                            ),
                                                            stage.originCountryCode,
                                                            stage.circuitName,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(" · "),
                                                    )
                                                    .join("\n")
                                            :   t(
                                                    "scorecard.transport.unavailable_tooltip",
                                                    "The transport contribution could not be calculated because the origin is unknown.",
                                                )
                                        }
                                    >
                                        <Stack
                                            gap={0.5}
                                            alignItems="center"
                                            justifyContent="center"
                                            sx={{ marginTop: theme.spacing(1) }}
                                        >
                                            {transportScore.status === "calculated" ?
                                                <>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ color: "text.secondary", lineHeight: 1.2 }}
                                                    >
                                                        {t(
                                                            "scorecard.transport.compact",
                                                            "Transport: {{value}} {{unit}}",
                                                            {
                                                                value: transportValue,
                                                                unit: transportUnit,
                                                            },
                                                        )}
                                                    </Typography>
                                                    <Stack
                                                        direction="row"
                                                        gap={0.5}
                                                        justifyContent="center"
                                                        flexWrap="wrap"
                                                    >
                                                        {resolveTransportLegDisplays(transportScore.stages).map(
                                                            ({ stage, leg, index }) => (
                                                                <Chip
                                                                    key={`${stage.stage}-${leg.name ?? index}-${leg.distanceKm ?? "_"}`}
                                                                    size="small"
                                                                    icon={resolveLegIcon(leg)}
                                                                    label={resolveLegLabel(leg, stage)}
                                                                    variant="outlined"
                                                                    sx={{
                                                                        height: 22,
                                                                        "& .MuiChip-icon": {
                                                                            fontSize: 16,
                                                                            marginLeft: 0.75,
                                                                            marginRight: -0.25,
                                                                        },
                                                                        "& .MuiChip-label": {
                                                                            fontSize: "0.75rem",
                                                                            paddingLeft: 0.75,
                                                                            paddingRight: 1,
                                                                        },
                                                                    }}
                                                                />
                                                            ),
                                                        )}
                                                    </Stack>
                                                </>
                                            :   <Typography
                                                    variant="caption"
                                                    sx={{ color: "text.secondary", lineHeight: 1.2 }}
                                                >
                                                    {t(
                                                        "scorecard.transport.unavailable",
                                                        "Transport not calculated: unknown origin",
                                                    )}
                                                </Typography>
                                            }
                                        </Stack>
                                    </Tooltip>
                                )}
                            </Suspense>
                        </ScoreCard>
                    </Box>,
                ];
            })}
        </Box>
    );
};
